import React, { useEffect, useState } from 'react';
import { adminGetMonthlyFinance, adminGetMonthProjection } from '../../lib/api';
import { Download, Loader2, Wallet, TrendingUp, TrendingDown } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

export default function MonthlyMoney() {
  const [data, setData] = useState(null);
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGetMonthlyFinance().then(setData).catch(() => {}).finally(() => setLoading(false));
    adminGetMonthProjection().then(setProj).catch(() => {});
  }, []);

  const downloadCsv = () => {
    if (!data) return;
    const head = ['Month', 'Paid Orders', 'Money In', 'Product Cost', 'Shipping Collected', 'Discounts Given', 'Surcharges Collected', 'Store Credit Used', 'Net Profit', 'GST Estimate (1/11)'];
    const rows = data.months.map((r) => [monthLabel(r.month), r.orders, r.moneyIn, r.cogs, r.shipping, r.discounts, r.surcharges, r.creditUsed, r.netProfit, r.gstEstimate]);
    rows.push(['TOTAL', data.totals.orders, data.totals.moneyIn, data.totals.cogs, data.totals.shipping, data.totals.discounts, data.totals.surcharges, data.totals.creditUsed, data.totals.netProfit, data.totals.gstEstimate]);
    const csv = [head, ...rows].map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `alp-monthly-finance-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  if (loading) return <div className="py-20 text-center text-alp-gray"><Loader2 className="animate-spin inline" size={20} /></div>;
  if (!data || data.months.length === 0) return <p className="text-alp-gray py-16 text-center">No paid orders yet — months will appear here as money lands.</p>;

  const cols = [
    { k: 'moneyIn', label: 'Money In', cls: 'text-emerald-300' },
    { k: 'cogs', label: 'Product Cost', cls: 'text-red-300' },
    { k: 'shipping', label: 'Shipping', cls: 'text-alp-cream/80' },
    { k: 'discounts', label: 'Discounts', cls: 'text-alp-cream/80' },
    { k: 'netProfit', label: 'Net Profit', cls: 'text-alp-gold font-semibold' },
    { k: 'gstEstimate', label: 'GST (1/11)', cls: 'text-alp-gray' },
  ];

  return (
    <div data-testid="monthly-money-panel">
      <div className="flex justify-between items-start flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2"><Wallet size={20} className="text-alp-gold" /> Monthly Money</h2>
          <p className="text-alp-gray text-sm mt-1">Cash basis &mdash; paid orders only. Net profit = money in minus product cost. GST shown as a 1/11 estimate if registered.</p>
        </div>
        <button onClick={downloadCsv} data-testid="monthly-money-csv" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold rounded hover:bg-alp-goldlight transition-colors">
          <Download size={14} /> Download CSV
        </button>
      </div>

      {proj && (
        <div className="bg-alp-card/60 border border-alp-gold/40 rounded-lg p-5 mb-6" data-testid="month-projection">
          <div className="text-[10px] uppercase tracking-[0.2em] text-alp-gold mb-3">This month's trajectory — day {proj.dayOfMonth} of {proj.daysInMonth}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-alp-gray">Month to date</div>
              <div className="text-xl font-semibold text-alp-cream">{fmt(proj.mtdRevenue)}</div>
              <div className="text-alp-gray text-xs">{proj.mtdOrders} paid orders</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-alp-gray">Projected month total</div>
              <div className="text-xl font-semibold text-alp-gold">{fmt(proj.projectedRevenue)}</div>
              <div className="text-alp-gray text-xs">at current daily pace</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-alp-gray">Last month actual</div>
              <div className="text-xl font-semibold text-alp-cream">{fmt(proj.prevRevenue)}</div>
              <div className="text-alp-gray text-xs">{proj.prevOrders} paid orders</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-alp-gray">Tracking vs last month</div>
              {proj.vsPrevPct === null ? (
                <div className="text-sm text-alp-gray mt-1.5">No prior month data yet</div>
              ) : (
                <div className={`text-xl font-semibold inline-flex items-center gap-1.5 ${proj.vsPrevPct >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                  {proj.vsPrevPct >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                  {proj.vsPrevPct >= 0 ? '+' : ''}{proj.vsPrevPct}%
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[['Money in (all time)', fmt(data.totals.moneyIn), 'text-emerald-300'],
          ['Product cost', fmt(data.totals.cogs), 'text-red-300'],
          ['Net profit', fmt(data.totals.netProfit), 'text-alp-gold'],
          ['Paid orders', data.totals.orders, 'text-alp-cream']].map(([l, v, c]) => (
          <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">{l}</p>
            <p className={`text-xl ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="border border-alp-line rounded-lg overflow-x-auto bg-alp-card/30">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-alp-line text-[10px] tracking-[0.15em] uppercase text-alp-gray">
              <th className="text-left px-4 py-3">Month</th>
              <th className="text-right px-3 py-3">Orders</th>
              {cols.map((c) => <th key={c.k} className="text-right px-3 py-3">{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-alp-line/50">
            {data.months.map((r) => (
              <tr key={r.month} data-testid={`money-row-${r.month}`} className="hover:bg-alp-card/50 transition-colors">
                <td className="px-4 py-3 text-alp-cream">{monthLabel(r.month)}</td>
                <td className="px-3 py-3 text-right text-alp-cream/80">{r.orders}</td>
                {cols.map((c) => <td key={c.k} className={`px-3 py-3 text-right ${c.cls}`}>{fmt(r[c.k])}</td>)}
              </tr>
            ))}
            <tr className="border-t-2 border-alp-gold/40 bg-alp-gold/5" data-testid="money-totals-row">
              <td className="px-4 py-3 text-alp-gold font-semibold uppercase text-xs tracking-[0.15em]">Total</td>
              <td className="px-3 py-3 text-right text-alp-cream">{data.totals.orders}</td>
              {cols.map((c) => <td key={c.k} className={`px-3 py-3 text-right ${c.cls}`}>{fmt(data.totals[c.k])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
